import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GetCommunityBlockedUsersDocument,
  GetCommunityBlockedUsersQuery,
  GetCommunityBlockedUsersQueryVariables,
  GetCommunityReportedContentDocument,
  GetCommunityReportedContentQuery,
  GetCommunityReportedContentQueryVariables,
} from 'generated/graphql';
import { makeAsync } from '@client/new/helpers/async';
import { Async, toAsyncError, toAsyncFetched } from '@client/new/helpers/types';
import { CommunityId } from '../model/community.types';
import { toReplyId } from '../model/reply.types';
import { toPostId } from '../model/post.types';
import {
  toBlockedUsers,
  toReportedItems,
  toReportedPosts,
  withoutBlockedUser,
  withoutReportedPost,
} from '../model/moderation.mappers';
import { useBusyKeys } from '../lib/useBusyKeys';
import { analytics } from '../lib/analytics';
import {
  BlockedCommunityUser,
  ModerationFeed,
  ModerationMode,
  postKey,
  ReportedPost,
  ReportedPostItem,
  userKey,
} from '../model/moderation.types';
import { useBlockUser } from './useBlockUser';
import { useDeletePost } from './useDeletePost';
import { useDeleteReply } from './useDeleteReply';
import { useKeepContent } from './useKeepContent';
import { useUnblockUser } from './useUnblockUser';

type UseCommunityModeration = {
  mode: ModerationMode;
  setMode: (mode: ModerationMode) => void;
  reported: Async<ReportedPostItem[]>;
  blocked: Async<BlockedCommunityUser[]>;
  isBusy: (key: string) => boolean;
  approve: (post: ReportedPost) => Promise<void>;
  remove: (post: ReportedPost) => Promise<void>;
  blockAuthor: (post: ReportedPost) => Promise<void>;
  unblock: (user: BlockedCommunityUser) => Promise<void>;
};

const keepFetchedDataWhileReloading = (
  current: Async<ModerationFeed>,
): Async<ModerationFeed> =>
  current.type === 'fetched' ? current : makeAsync.fetching();

export const useCommunityModeration = (
  communities: CommunityId[],
): UseCommunityModeration => {
  const client = useApolloClient();
  const [mode, setMode] = useState<ModerationMode>('reported');
  const [feed, setFeed] = useState<Async<ModerationFeed>>(
    makeAsync.notFetched(),
  );

  const latestLoadId = useRef(0);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const isStaleLoad = useCallback(
    (loadId: number) => !mounted.current || latestLoadId.current !== loadId,
    [],
  );

  const load = useCallback(async () => {
    const loadId = ++latestLoadId.current;
    setFeed(keepFetchedDataWhileReloading);

    try {
      const perCommunity = await Promise.all(
        communities.map(async (communityId) => {
          const [reported, blocked] = await Promise.all([
            client.query<
              GetCommunityReportedContentQuery,
              GetCommunityReportedContentQueryVariables
            >({
              query: GetCommunityReportedContentDocument,
              variables: { communityId },
              fetchPolicy: 'network-only',
            }),
            client.query<
              GetCommunityBlockedUsersQuery,
              GetCommunityBlockedUsersQueryVariables
            >({
              query: GetCommunityBlockedUsersDocument,
              variables: { communityId },
              fetchPolicy: 'network-only',
            }),
          ]);

          return {
            reported: toReportedPosts(
              reported.data.reportedContent ?? [],
              communityId,
            ),
            blocked: toBlockedUsers(
              blocked.data.blockedUsers ?? [],
              communityId,
            ),
          };
        }),
      );

      if (isStaleLoad(loadId)) return;

      setFeed(
        makeAsync.fetched({
          reported: perCommunity.flatMap((entry) => entry.reported),
          blocked: perCommunity.flatMap((entry) => entry.blocked),
        }),
      );
    } catch (error) {
      if (isStaleLoad(loadId)) return;

      setFeed(
        toAsyncError(
          error instanceof Error ? error.message : 'Failed to load moderation',
        ),
      );
    }
  }, [client, communities, isStaleLoad]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (mode === 'reported') {
      analytics.trackReportedPostsVisited();
    } else {
      analytics.trackBlockedUsersVisited();
    }
  }, [mode]);

  const invalidateInFlightLoads = useCallback(() => {
    latestLoadId.current += 1;
  }, []);

  const removeFromFeed = useCallback(
    (updater: (feed: ModerationFeed) => ModerationFeed) => {
      invalidateInFlightLoads();
      setFeed((current) =>
        current.type === 'fetched'
          ? makeAsync.fetched(updater(current.value))
          : current,
      );
    },
    [invalidateInFlightLoads],
  );

  const deletePost = useDeletePost();
  const deleteReply = useDeleteReply();
  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();
  const keepContent = useKeepContent();

  const refreshCounts = useCallback(
    () => client.refetchQueries({ include: ['GetCreatorCommunities'] }),
    [client],
  );

  const { isBusy, runExclusive } = useBusyKeys();

  const runUnlessInFlight = useCallback(
    (key: string, action: () => Promise<void>): Promise<void> =>
      runExclusive(key, async () => {
        await action();
        await Promise.all([load(), refreshCounts()]);
      }),
    [runExclusive, load, refreshCounts],
  );

  const reported: Async<ReportedPostItem[]> = useMemo(
    () =>
      feed.type === 'fetched'
        ? toAsyncFetched(toReportedItems(feed.value))
        : feed,
    [feed],
  );

  const blocked: Async<BlockedCommunityUser[]> = useMemo(
    () => (feed.type === 'fetched' ? toAsyncFetched(feed.value.blocked) : feed),
    [feed],
  );

  const approve = useCallback(
    (post: ReportedPost) => {
      analytics.trackReportedPostKept();
      return runUnlessInFlight(postKey(post), async () => {
        removeFromFeed((current) => withoutReportedPost(current, post));
        await keepContent({ reportIds: post.reportIds });
      });
    },
    [runUnlessInFlight, removeFromFeed, keepContent],
  );

  const remove = useCallback(
    (post: ReportedPost) => {
      analytics.trackReportedPostRemoved();
      return runUnlessInFlight(postKey(post), async () => {
        removeFromFeed((current) => withoutReportedPost(current, post));
        if (post.isReply) {
          await deleteReply({
            communityId: post.communityId,
            postId: post.parentPostId,
            replyId: toReplyId(post.activityId),
          });
        } else {
          await deletePost({
            communityId: post.communityId,
            postId: toPostId(post.activityId),
          });
        }
      });
    },
    [runUnlessInFlight, removeFromFeed, deleteReply, deletePost],
  );

  const blockAuthor = useCallback(
    (post: ReportedPost) => {
      analytics.trackUserBlocked('moderation');
      return runUnlessInFlight(postKey(post), () =>
        blockUser({ communityId: post.communityId, userId: post.authorId }),
      );
    },
    [runUnlessInFlight, blockUser],
  );

  const unblock = useCallback(
    (user: BlockedCommunityUser) => {
      analytics.trackUserUnblocked();
      return runUnlessInFlight(userKey(user), async () => {
        removeFromFeed((current) => withoutBlockedUser(current, user));
        await unblockUser({
          communityId: user.communityId,
          userId: user.userId,
        });
      });
    },
    [runUnlessInFlight, removeFromFeed, unblockUser],
  );

  return {
    mode,
    setMode,
    reported,
    blocked,
    isBusy,
    approve,
    remove,
    blockAuthor,
    unblock,
  };
};
